import { convertDateToIsoString } from '@helpers/dates'
import { FormHelperText, Stack } from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers'
import {
	type ComponentProps,
	type ReactNode,
	useCallback,
	useMemo,
} from 'react'
import {
	type Control,
	Controller,
	type ControllerFieldState,
	type ControllerRenderProps,
	type FieldPath,
	type FieldValues,
} from 'react-hook-form'
import { useTranslation } from 'react-i18next'

interface Props<
	TFieldValues extends FieldValues,
	TName extends FieldPath<TFieldValues>,
> {
	control: Control<TFieldValues>
	name: TName
	isRequired?: boolean
	label?: ReactNode
	invalidDateLabel?: ReactNode
	onChange?: (value: string | null) => void
	maxDate?: Date | string
	externalError?: string
}

const DatePickerWithMultipleOnChange = ({
	onChange,
	onChange2,
	...props
}: Omit<ComponentProps<typeof DatePicker>, 'onChange'> & {
	onChange?: (date: string | null) => void
	onChange2?: (date: string | null) => void
}) => {
	const handleChange = useCallback(
		(value: Date | string | null) => {
			const dateAsString = (value && convertDateToIsoString(value)) ?? null
			onChange?.(dateAsString)
			onChange2?.(dateAsString)
		},
		[onChange, onChange2]
	)
	return <DatePicker {...props} onChange={handleChange} />
}

// A date picker controller that stores dates as ISO strings in the form state.
const DatePickerController = <
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
	control,
	name,
	invalidDateLabel,
	isRequired,
	label,
	onChange,
	maxDate,
	externalError,
}: Props<TFieldValues, TName>) => {
	const { t } = useTranslation()
	const maxDateAsDate = useMemo(
		() => (maxDate ? new Date(maxDate) : undefined),
		[maxDate]
	)
	const renderFunc = useCallback(
		({
			field,
			fieldState,
		}: {
			field: ControllerRenderProps<TFieldValues, TName>
			fieldState: ControllerFieldState
		}) => {
			const valueAsDate = field.value ? new Date(field.value) : null
			const isDateInvalid =
				valueAsDate && maxDateAsDate && valueAsDate > maxDateAsDate
			const hasError = !!fieldState.error || !!externalError
			return (
				<Stack>
					<DatePickerWithMultipleOnChange
						value={valueAsDate}
						format="yyyy-MM-dd"
						maxDate={maxDateAsDate}
						onChange={onChange}
						onChange2={field.onChange}
						label={label}
						ref={field.ref}
						slotProps={{
							textField: {
								variant: 'standard',
								error: hasError,
							},
						}}
					/>
					{(fieldState.error || externalError) && (
						<FormHelperText error>
							{externalError ??
								(fieldState.error?.type === 'required'
									? t('input.required')
									: fieldState.error?.message)}
						</FormHelperText>
					)}
					{isDateInvalid && invalidDateLabel && (
						<FormHelperText error>{invalidDateLabel}</FormHelperText>
					)}
				</Stack>
			)
		},
		[label, maxDateAsDate, invalidDateLabel, onChange, t, externalError]
	)
	return (
		<Controller
			name={name}
			control={control}
			rules={{ required: isRequired }}
			render={renderFunc}
		/>
	)
}

export { DatePickerController }
